import React, { useState, useRef, useEffect, CSSProperties } from 'react';

interface TooltipProps {
  children: React.ReactNode;
  text: string;
  tooltipPosition?: 'top' | 'bottom' | 'left' | 'right';
}

interface Coords {
  top: number;
  left: number;
  width: number;
  height: number;
}

function Tooltip({ children, text, tooltipPosition = 'top' }: TooltipProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [coords, setCoords] = useState<Coords>({
    top: 0,
    left: 0,
    width: 0,
    height: 0
  });
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const updatePosition = () => {
      if (wrapperRef.current) {
        const rect = wrapperRef.current.getBoundingClientRect();
        setCoords({
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height
        });
      }
    };

    if (isVisible) {
      updatePosition(); // measure immediately when shown
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
    }

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isVisible]);

  const handleMouseEnter = () => {
    if (wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      setCoords({
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height
      });
    }
    setIsVisible(true);
  };

  const handleMouseLeave = () => {
    setIsVisible(false);
  };

  const getPositionStyles = (): {
    container: CSSProperties;
    arrowClass: string;
    arrowStyle: CSSProperties;
  } => {
    const scrollY = window.scrollY;
    const scrollX = window.scrollX;

    switch (tooltipPosition) {
      case 'top':
        // Tooltip above the target element, arrow pointing down
        return {
          container: {
            position: 'absolute',
            top: coords.top + scrollY - 8,
            left: coords.left + scrollX + coords.width / 2,
            transform: 'translate(-50%, -100%)'
          },
          arrowClass: 'absolute w-2 h-2 bg-indigo-700 transform',
          arrowStyle: {
            // Place arrow at bottom center of tooltip
            bottom: '-4px',
            left: '50%',
            transform: 'translateX(-50%) rotate(45deg)'
          }
        };

      case 'bottom':
        // Tooltip below the target element, arrow pointing up
        return {
          container: {
            position: 'absolute',
            top: coords.top + coords.height + 8 + scrollY,
            left: coords.left + (coords.width / 2) + scrollX,
            transform: 'translate(-50%, 0)'
          },
          arrowClass: 'absolute w-2 h-2 bg-indigo-700 transform',
          arrowStyle: {
            // Place arrow at top center of tooltip
            top: '-4px',
            left: '50%',
            // rotate(225deg) flips the diamond to point upwards
            transform: 'translateX(-50%) rotate(225deg)'
          }
        };

      case 'left':
        // Tooltip to the left, arrow pointing right
        return {
          container: {
            position: 'absolute',
            top: coords.top + (coords.height / 2) + scrollY,
            left: coords.left + scrollX - 8,
            transform: 'translate(-100%, -50%)'
          },
          arrowClass: 'absolute w-2 h-2 bg-indigo-700 transform',
          arrowStyle: {
            // Place arrow at right center of tooltip
            right: '-4px',
            top: '50%',
            // rotate(135deg) flips the diamond to point right
            transform: 'translateY(-50%) rotate(135deg)'
          }
        };

      case 'right':
        // Tooltip to the right, arrow pointing left
        return {
          container: {
            position: 'absolute',
            top: coords.top + (coords.height / 2) + scrollY,
            left: coords.left + coords.width + 8 + scrollX,
            transform: 'translate(0, -50%)'
          },
          arrowClass: 'absolute w-2 h-2 bg-indigo-700 transform',
          arrowStyle: {
            // Place arrow at left center of tooltip
            left: '-4px',
            top: '50%',
            // rotate(315deg) flips the diamond to point left
            transform: 'translateY(-50%) rotate(315deg)'
          }
        };

      default:
        // Fallback to 'top'
        return {
          container: {
            position: 'absolute',
            top: coords.top + scrollY - 8,
            left: coords.left + scrollX + coords.width / 2,
            transform: 'translate(-50%, -100%)'
          },
          arrowClass: 'absolute w-2 h-2 bg-indigo-700 transform',
          arrowStyle: {
            bottom: '-4px',
            left: '50%',
            transform: 'translateX(-50%) rotate(45deg)'
          }
        };
    }
  };

  const { container, arrowClass, arrowStyle } = getPositionStyles();

  return (
    <>
      <div
        ref={wrapperRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className="inline-block"
      >
        {children}
      </div>

      {isVisible && (
        <div
          role="tooltip"
          className="relative z-50 text-sm font-medium text-white bg-indigo-700 rounded-lg shadow-md transition-opacity duration-300"
          style={container}
        >
          <div className="px-2 py-2">
            {text}
          </div>

          {/* The arrow diamond */}
          <div
            className={arrowClass}
            style={arrowStyle}
          />
        </div>
      )}
    </>
  );
}

export default Tooltip;